/**
 * A tela Hoje: o dia da mãe em uma tela.
 *
 * Ordem de composição, fixada em docs/design/DESIGN.md: saudação → uma frase
 * da Ruth sobre o dia → cartão da próxima dose → "Seu dia" com hora à esquerda
 * → atalho de compras.
 *
 * A linha do dia mistura três coisas em ordem de hora: tarefa do plano, bloco
 * fixo da rotina e compromisso. A rotina de todo dia (louça, lixo) fica de
 * fora — ela mora na tela A casa, como a tela Semana mostra.
 *
 * Nenhuma barra de progresso, nenhuma porcentagem, nenhum contador — exceto
 * "dose 9 de 21", onde contar é cuidado. A bolinha que ela marca é dela e não
 * vira placar: dia sem marca não é dia falhado, é dia sem resposta, e pode
 * ficar sem resposta para sempre.
 *
 * ## O dia que ela está vendo pode não ser hoje
 *
 * "Posso querer ver o histórico, a agenda nos dias anteriores." A Semana já
 * andava no tempo; esta não, e não havia lugar nenhum no app que mostrasse um
 * **dia inteiro** que já passou — com as horas, os blocos e os compromissos.
 * Quem anda pelos dias é o `App.tsx`, que pede o dia ao servidor; aqui chega
 * `dia.hoje` (o dia que ela está vendo) e `hojeNaCasa` (o dia de verdade).
 *
 * A tela inteira foi escrita no presente, e a linha que separa o que sobrevive
 * é uma só: **o que é resposta ao agora só existe em hoje; o que é memória
 * existe em qualquer dia.** A dose por vir, o "mais tarde", o convite de pôr
 * uma coisa e o atalho do mercado falam do agora. A marca — "fui sim ao
 * pediatra na terça" — é memória, e é justamente por ela que a mãe volta.
 *
 * **E um dia que passou não vira boletim.** Mostrar o que ela marcou é lembrar
 * por ela; transformar o que ela não marcou em juízo seria medi-la (regra 2).
 * Então o dia antigo é desenhado igualzinho ao de hoje, menos o que virou
 * pergunta sem resposta possível: nada fica vermelho, nada diz "não fez", nada
 * conta, e a bolinha vazia de três semanas atrás continua sendo o convite de
 * dizer "fui", não a marca do que faltou.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  api,
  dataPorExtenso,
  fraseDoErro,
  hhmm,
  type Bloco,
  type BlocoDoDia,
  type Compromisso,
  type CompromissoMarcado,
  type CompromissoNovo,
  type Dia,
  type Dose,
  type ItemDoDia,
} from '../api'
import { FormularioDoBloco } from '../componentes/BlocoDoDia'
import { ConsertoDoCompromisso } from '../componentes/CorrecaoDoCompromisso'
import { Icone } from '../componentes/Icone'

type Props = {
  dia: Dia
  /**
   * O dia de hoje na casa, dito pelo servidor — e não o que ela está vendo,
   * que é o `dia.hoje`. Os dois nomes se parecem porque a API chama de "hoje"
   * o dia que devolve; comparar um com o outro é o que faz a tela saber que
   * está num sábado que já passou.
   *
   * Opcional porque a tela nasceu sendo só de hoje: sem ele, o dia que ela vê
   * é hoje, e tudo se comporta como sempre se comportou.
   */
  hojeNaCasa?: string | null
  aoMarcarFeito: (ocorrencia: string) => void
  /** Desfazer é parte de poder marcar, e mais ainda em dia antigo. */
  aoDesmarcar: (ocorrencia: string) => void
  aoAdiar: (ocorrencia: string) => void
  aoAbrirCompras: () => void
  /** Consertar um bloco muda o dia inteiro: quem monta a tela recarrega. */
  aoMudarACasa: () => void
}

type LinhaDoDia =
  | { tipo: 'tarefa'; minuto: number; item: ItemDoDia }
  | { tipo: 'bloco'; minuto: number; bloco: Bloco }
  | { tipo: 'compromisso'; minuto: number; compromisso: Compromisso }

function emMinutos(hora: string | null): number {
  if (!hora) return 24 * 60
  const [h, m] = hora.split(':').map(Number)
  return h * 60 + m
}

/** Tarefas, blocos e compromissos de hoje, em ordem de hora. */
export function montarODia(dia: Dia): LinhaDoDia[] {
  const linhas: LinhaDoDia[] = [
    ...dia.plano.map(
      (item): LinhaDoDia => ({ tipo: 'tarefa', minuto: emMinutos(item.hora), item }),
    ),
    ...dia.blocos.map(
      (bloco): LinhaDoDia => ({ tipo: 'bloco', minuto: emMinutos(bloco.inicio), bloco }),
    ),
    ...dia.compromissos
      .filter((c) => c.quando === dia.hoje)
      .map(
        (compromisso): LinhaDoDia => ({
          tipo: 'compromisso',
          minuto: emMinutos(compromisso.hora),
          compromisso,
        }),
      ),
  ]

  return linhas.sort((a, b) => a.minuto - b.minuto)
}

function proximaDose(doses: Dose[]): Dose | null {
  return doses.find((dose) => !dose.dada) ?? null
}

function nomeDoBloco(bloco: Bloco): string {
  if (bloco.descricao) return bloco.descricao
  const nomes: Record<string, string> = {
    trabalho: 'Trabalho',
    almoco: 'Almoço',
    escola_levar: 'Levar na escola',
    escola_buscar: 'Buscar na escola',
    sono_crianca: 'Soneca',
    mamada: 'Mamada',
    tempo_com_filhos: 'Tempo com as crianças',
    outro: 'Bloco do dia',
  }
  return nomes[bloco.tipo] ?? 'Bloco do dia'
}

/**
 * A próxima dose, e o botão que a registra.
 *
 * Este botão passou semanas mentindo: ele chamava `setDose(null)`, o cartão
 * sumia da tela e o servidor nunca ficava sabendo. A mãe dava o antibiótico da
 * criança, tocava "Dei", acreditava — e a dose continuava pendente, a contagem
 * "dose 9 de 21" não andava e o lembrete daquela dose não era silenciado. É o
 * botão mais sério do produto inteiro, o único lugar onde contar é cuidado
 * (regra 2), e era o único decorativo.
 *
 * Agora ele grava e espera a resposta. Se a rede falhar, o cartão fica onde
 * está e a frase diz — sumir com o cartão sem ter gravado é justamente o erro
 * que a mãe não tem como perceber.
 */
function CartaoDeDose({ dose, aoDar }: { dose: Dose; aoDar: (dose: Dose) => Promise<void> }) {
  const [dando, setDando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const dar = async () => {
    if (dando) return
    setDando(true)
    setErro(null)
    try {
      await aoDar(dose)
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui anotar agora. Tenta de novo?'))
      setDando(false)
    }
  }

  return (
    <div className="cartao dose">
      <div className="dose__icone">
        <Icone nome="remedio" />
      </div>
      <div className="dose__texto">
        {/* A dose fica na linha de baixo: no mockup ela cabe no título, mas
            num aparelho de 375px "Amoxicilina do Theo · 5 ml" quebra e deixa
            "5 ml" sozinho na segunda linha. */}
        <span className="dose__nome">
          {dose.remedio} do {dose.para_quem}
        </span>
        <span className="dose__meta">
          {dose.dose} · às {hhmm(dose.hora)} · dose {dose.ordem} de {dose.total}
        </span>
        {erro && (
          <span className="dose__erro" role="alert">
            {erro}
          </span>
        )}
      </div>
      <button type="button" className="botao-primario" disabled={dando} onClick={() => void dar()}>
        {dando ? 'Anotando…' : 'Dei'}
      </button>
    </div>
  )
}

/**
 * Uma coisa da casa no dia dela: a bolinha do "já fiz" e o "mais tarde".
 *
 * A bolinha vale de hoje para trás, e é o motivo de a mãe voltar num dia
 * antigo. Num dia que ainda não chegou ela não aparece: "já fiz" é uma
 * pergunta sem resposta possível na quinta que vem, e uma coluna de bolinhas
 * vazias em dias futuros é a silhueta de um checklist esperando ser
 * preenchido — a forma do boletim, mesmo sem nenhuma palavra que meça.
 */
function Tarefa({
  item,
  jaFoi,
  ehHoje,
  aoMarcarFeito,
  aoDesmarcar,
  aoAdiar,
}: {
  item: ItemDoDia
  /** O dia já chegou. Não se diz "já fiz" ao que ainda não aconteceu. */
  jaFoi: boolean
  /** É o dia que ela está vivendo. */
  ehHoje: boolean
  aoMarcarFeito: (id: string) => void
  aoDesmarcar: (id: string) => void
  aoAdiar: (id: string) => void
}) {
  return (
    <div className={`linha ${item.feito ? 'linha--feita' : ''}`}>
      <span className="linha__hora">{hhmm(item.hora)}</span>
      <div className="linha__corpo">
        {/* Marcada, a bolinha aparece em qualquer dia: o que ela mesma marcou
            fica onde ela deixou. */}
        {(jaFoi || item.feito) && (
          <button
            type="button"
            className={`marcador ${item.feito ? 'marcador--feito' : ''}`}
            // A tarefa era a única marca do app sem volta: o compromisso
            // desfazia, a dose desfazia, e esta era definitiva. Com os dias
            // antigos abertos ficou pior — um toque errado num sábado de agosto
            // não tinha saída pela tela, e quem não consegue desfazer aprende a
            // não tocar.
            aria-label={
              item.feito ? `tirar a marca de ${item.nome}` : `marcar ${item.nome} como feito`
            }
            aria-pressed={item.feito}
            onClick={() => item.id && (item.feito ? aoDesmarcar(item.id) : aoMarcarFeito(item.id))}
          >
            {item.feito && <Icone nome="check" tamanho={14} />}
          </button>
        )}
        <span className="linha__texto">
          <span className="linha__nome">{item.nome}</span>
        </span>
        {/* "Mais tarde" é a resposta da regra 5 a uma tarefa que está chamando
            ela agora, e empurra a hora dentro do próprio dia. Num dia que já
            foi não se adia nada, e num dia que ainda vem nada está chamando —
            oferecer o adiamento ali seria inventar uma pressão que não existe.
            Mudar a hora de uma coisa de outro dia continua em A casa. */}
        {ehHoje && !item.feito && item.id && (
          <button type="button" className="mais-tarde" onClick={() => aoAdiar(item.id!)}>
            mais tarde
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Um pedaço fixo do dia. Tocar abre o conserto, ali mesmo.
 *
 * "Não consigo editar nenhuma das telas em anexo." Esta era uma delas: o dia
 * dela inteiro desenhado — academia, escola, home office, almoço — e nada para
 * tocar. Quando a primeira conversa entende errado, é aqui que ela vê, todo
 * dia, e era daqui que ela tinha que sair para consertar.
 */
function BlocoDaRotina({
  bloco,
  aberto,
  erro,
  aoAbrir,
  aoFechar,
  aoMexer,
}: {
  bloco: Bloco
  aberto: boolean
  /** O que não deu nesta linha. Fica embaixo dela, onde o dedo já está. */
  erro: string | null
  aoAbrir: () => void
  aoFechar: () => void
  aoMexer: (chamada: Promise<unknown>) => Promise<void>
}) {
  const protegido = bloco.protegido
  const [inteiro, setInteiro] = useState<BlocoDoDia | null>(null)

  useEffect(() => {
    // Fechou, esquece o que buscou. Guardar entre uma abertura e outra foi o
    // que fez o formulário mostrar um bloco e a linha mostrar outro: ela
    // apagava "Retornar e tomar café", o dia recarregava sem ele, e a cópia
    // velha continuava aqui — o "Pode tirar" seguinte ia atrás de um bloco que
    // já não existia e voltava "não achei esse bloco".
    if (!aberto) {
      setInteiro(null)
      return
    }
    if (!bloco.id) return

    // A linha traz a hora e a identidade, não os dias. Montar um objeto pela
    // metade abriria o formulário dizendo que o bloco não acontece em dia
    // nenhum, e ela salvaria isso.
    let vivo = true
    void api
      .configuracoes()
      .then((c) => {
        if (vivo) setInteiro(c.blocos.find((b) => b.id === bloco.id) ?? null)
      })
      .catch(() => {
        if (vivo) setInteiro(null)
      })
    return () => {
      vivo = false
    }
  }, [aberto, bloco.id])

  return (
    <div className="linha">
      <span className="linha__hora">{hhmm(bloco.inicio)}</span>
      <div className="linha__pilha">
        <button
          type="button"
          className={`linha__corpo linha__toque tocavel ${
            protegido ? 'linha__corpo--protegido' : 'linha__corpo--fixo'
          }`}
          aria-label={`Consertar ${nomeDoBloco(bloco)}`}
          onClick={aoAbrir}
        >
          {protegido && <Icone nome="protegido" tamanho={18} />}
          <span className="linha__texto">
            <span className="linha__nome">{nomeDoBloco(bloco)}</span>
            {protegido && (
              <span className="linha__meta">até {hhmm(bloco.fim)} · nada entra aqui</span>
            )}
          </span>
          <span className="tocavel__seta">
            <Icone nome="seta" tamanho={16} />
          </span>
        </button>

        {/* A frase vem antes do formulário, e não depois: o formulário do bloco
            tem sete caixinhas de dia e desce meia tela. Quem tocou em "Tirar
            este bloco" lá em cima não veria uma frase escrita lá embaixo. */}
        {erro && (
          <p className="agenda__erro" role="alert">
            {erro}
          </p>
        )}

        {aberto &&
          (inteiro ? (
            <FormularioDoBloco
              bloco={inteiro}
              aoSalvar={(campos) => aoMexer(api.corrigirBloco(inteiro.id, campos))}
              aoFechar={aoFechar}
              aoTirar={() => aoMexer(api.tirarBloco(inteiro.id))}
            />
          ) : (
            <p className="linha__meta" role="status">
              Abrindo…
            </p>
          ))}
      </div>
    </div>
  )
}

/**
 * Um compromisso de hoje: a bolinha do "fui", e o toque que abre o conserto.
 *
 * Era a única linha da tela que não abria nada — e era a que mais parecia
 * clicável, porque o cartão branco com borda tem mais relevo que as outras.
 * Pior: é exatamente aqui que o exemplo do Carlos acontece. "Amanhã é feriado,
 * as crianças não terão aula" — amanhã ela vê isso nesta linha, e tinha que
 * descobrir sozinha que a mesma linha, noutra tela, abria.
 *
 * A bolinha é a mesma da tarefa, no mesmo lugar do cartão: a mãe não deveria
 * aprender dois jeitos de dizer "fiz". E é por data, não por compromisso —
 * numa série, ter ido na terça não diz nada sobre a quinta.
 *
 * Nada mede a mãe (regra 2): linha sem bolinha marcada não fica vermelha, não
 * vira "pendente" e não entra em conta nenhuma. Ela pode não responder nunca.
 */
function CompromissoDoDia({
  compromisso,
  data,
  jaFoi,
  aberto,
  erro,
  aoAbrir,
  aoFechar,
  aoMexer,
}: {
  compromisso: Compromisso
  data: string
  /** O dia já chegou. Não se vai a uma coisa que ainda não aconteceu. */
  jaFoi: boolean
  aberto: boolean
  erro: string | null
  aoAbrir: () => void
  aoFechar: () => void
  aoMexer: (chamada: Promise<unknown>) => Promise<void>
}) {
  const [inteiro, setInteiro] = useState<CompromissoMarcado | null>(null)

  useEffect(() => {
    // Mesma regra do bloco: fechou, esquece. Ver `BlocoDaRotina`.
    if (!aberto) {
      setInteiro(null)
      return
    }
    if (!compromisso.id) return

    // A linha de hoje traz título, hora e lugar — não os dias da repetição.
    // Sem buscar o compromisso inteiro, o formulário abriria dizendo que a aula
    // não se repete, e ela salvaria isso por cima da série.
    let vivo = true
    void api
      .compromissos()
      .then(({ compromissos }) => {
        if (vivo) setInteiro(compromissos.find((c) => c.id === compromisso.id) ?? null)
      })
      .catch(() => {
        if (vivo) setInteiro(null)
      })
    return () => {
      vivo = false
    }
  }, [aberto, compromisso.id])

  const dizer = () => {
    if (!compromisso.id) return
    void aoMexer(
      compromisso.cumprido
        ? api.compromissoDescumprido(compromisso.id, compromisso.quando)
        : api.compromissoCumprido(compromisso.id, compromisso.quando),
    )
  }

  return (
    <div className={`linha ${compromisso.cumprido ? 'linha--feita' : ''}`}>
      <span className="linha__hora">{hhmm(compromisso.hora)}</span>
      <div className="linha__pilha">
        {/* O cartão deixou de ser um botão só para caber a bolinha ao lado do
            toque que abre o conserto: botão dentro de botão o navegador não
            entende, e um dos dois pararia de responder. */}
        <div className="linha__corpo">
          {/* A mesma linha da tela Compromissos: "fui" só de hoje para trás,
              porque na terça que vem a pergunta não tem resposta; tirar a
              marca vale sempre, porque um toque errado não pode ficar preso —
              e é por isso que a bolinha marcada aparece em qualquer dia. */}
          {(jaFoi || compromisso.cumprido) && (
            <button
              type="button"
              className={`marcador ${compromisso.cumprido ? 'marcador--feito' : ''}`}
              aria-label={
                compromisso.cumprido
                  ? `${compromisso.titulo}, fui`
                  : `marcar que fui, ${compromisso.titulo}`
              }
              aria-pressed={compromisso.cumprido}
              onClick={dizer}
            >
              {compromisso.cumprido && <Icone nome="check" tamanho={14} />}
            </button>
          )}

          <button
            type="button"
            className="linha__toque--dentro tocavel"
            aria-label={`Consertar ${compromisso.titulo}`}
            onClick={aoAbrir}
          >
            <span className="linha__icone">
              <Icone nome="calendario" />
            </span>
            <span className="linha__texto">
              <span className="linha__nome">{compromisso.titulo}</span>
              {compromisso.local && <span className="linha__meta">{compromisso.local}</span>}
            </span>
            <span className="tocavel__seta">
              <Icone nome="seta" tamanho={16} />
            </span>
          </button>
        </div>

        {/* Antes do formulário, colada na linha: é onde o dedo dela estava,
            tanto no toque da bolinha quanto no "nesse dia não tem". */}
        {erro && (
          <p className="agenda__erro" role="alert">
            {erro}
          </p>
        )}

        {aberto &&
          (inteiro ? (
            <ConsertoDoCompromisso
              compromisso={inteiro}
              data={data}
              aoSalvar={(campos) => aoMexer(api.corrigirCompromisso(inteiro.id, campos))}
              aoPular={(dia) => aoMexer(api.pularODia(inteiro.id, dia))}
              aoFechar={aoFechar}
            />
          ) : (
            <p className="linha__meta" role="status">
              Abrindo…
            </p>
          ))}
      </div>
    </div>
  )
}

/**
 * Pôr uma coisa no dia, a partir do dia.
 *
 * "Não consigo lançar atividades nos dias. Não existe botão de inserir nada na
 * agenda." Existia — três telas adiante, em Mais → Compromissos → "Marcar uma
 * coisa". Quem está olhando o dia e quer pôr uma coisa nele não deveria sair
 * do dia (regra 7), nem digitar a data de hoje: ela vem pronta de `dia.hoje`.
 *
 * Só o que é, a hora e o lugar. Repetição e lembrete continuam na tela
 * Compromissos: sete caixinhas de dia aqui seriam sete decisões a mais para
 * quem está com criança no colo.
 */
function Acrescentar({
  dia,
  ehHoje,
  aoMarcar,
  aoFechar,
}: {
  dia: Dia
  /** É o dia que ela está vivendo. Muda a hora que o formulário já traz. */
  ehHoje: boolean
  aoMarcar: (novo: CompromissoNovo) => Promise<void>
  aoFechar: () => void
}) {
  const [titulo, setTitulo] = useState('')
  // Em hoje, a hora da casa, e não 9h fixo: se são 20h, o que ela está pondo
  // no dia é coisa da noite, e um formulário aberto em 9h marcaria no passado
  // sem ela ver. O `agora` vem do servidor — o relógio do aparelho não entra
  // aqui. Num dia que ainda vem, "agora" não quer dizer nada: às 20h de hoje,
  // 20h na quinta seria só o horário em que ela estava mexendo no celular, e
  // aí vale o 9h da manhã com que a Semana abre o mesmo formulário.
  const [hora, setHora] = useState(ehHoje ? (dia.agora ?? '09:00:00').slice(0, 5) : '09:00')
  const [local, setLocal] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando || !titulo.trim()) return

    setSalvando(true)
    setErro(null)
    try {
      await aoMarcar({
        titulo: titulo.trim(),
        quando: dia.hoje,
        hora: `${hora}:00`,
        local: local.trim() || null,
      })
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui marcar agora. Tenta de novo?'))
      setSalvando(false)
    }
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      {/* A data só aparece quando não é hoje — e aí ela é o título, como na
          Semana. O dia está escrito lá no alto da tela, mas num dia cheio o
          alto já rolou para fora quando ela chega neste botão. */}
      {!ehHoje && <p className="linha__meta">{dataPorExtenso(dia.hoje)}</p>}

      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Pediatra da Ana"
          maxLength={160}
          autoFocus
        />
      </label>

      <label className="conf-pessoa__campo">
        <span>Que hora</span>
        <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
      </label>

      <label className="conf-pessoa__campo">
        <span>Onde</span>
        <input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="deixe vazio se não tem lugar"
          maxLength={200}
        />
      </label>

      {erro && (
        <p className="agenda__erro" role="alert">
          {erro}
        </p>
      )}

      <div className="conf-pessoa__acoes">
        <button type="submit" className="botao-primario" disabled={salvando || !titulo.trim()}>
          {salvando ? 'Marcando…' : 'Marcar'}
        </button>
        <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
          Deixa pra lá
        </button>
      </div>
    </form>
  )
}

export function Hoje({
  dia,
  hojeNaCasa,
  aoMarcarFeito,
  aoDesmarcar,
  aoAdiar,
  aoAbrirCompras,
  aoMudarACasa,
}: Props) {
  /** Qual linha está aberta para conserto. Uma de cada vez, bloco ou compromisso. */
  const [aberto, setAberto] = useState<string | null>(null)
  /**
   * O que não deu, e em qual linha.
   *
   * Guardar a linha junto é o que põe a frase embaixo do dedo dela. Uma frase
   * no alto da tela seria invisível para quem tocou no último compromisso do
   * dia — e invisível é igual a não ter frase nenhuma.
   */
  const [erro, setErro] = useState<{ onde: string; frase: string } | null>(null)
  const [acrescentando, setAcrescentando] = useState(false)

  /**
   * Mexer numa linha muda o dia inteiro: o planner encaixa tarefa nos buracos
   * entre os blocos, e tirar o feriado abre espaço. Em vez de adivinhar o
   * resultado, a tela pede o dia de novo.
   *
   * O `try` não é detalhe: sem ele, um `DELETE` que voltava 404 rejeitava a
   * promessa, o `setAberto(null)` nunca rodava e a tela não dizia nada. Nos
   * registros de produção esse 404 saiu quatro vezes seguidas para o mesmo
   * bloco — era o dono do produto tocando de novo, achando que a Ruth não
   * obedecia. Quando não dá, o formulário fica aberto de propósito, com a
   * frase embaixo: é ali que ela está olhando.
   */
  const aoMexer = useCallback(
    async (chamada: Promise<unknown>, onde: string) => {
      try {
        await chamada
        setAberto(null)
        setErro(null)
        aoMudarACasa()
      } catch (e) {
        setErro({ onde, frase: fraseDoErro(e, 'Não consegui mexer nisso agora. Tenta de novo?') })
      }
    },
    [aoMudarACasa],
  )

  /**
   * Anotar a dose e recarregar o dia.
   *
   * Recarregar em vez de esconder o cartão na mão: quem decide qual é a próxima
   * dose é o servidor, e é ele que sabe se aquela era a última.
   */
  const darADose = useCallback(
    async (dose: Dose) => {
      if (dose.id) await api.dei(dose.id)
      aoMudarACasa()
    },
    [aoMudarACasa],
  )

  /**
   * A coisa nova entra no dia pelo servidor, e o dia volta de lá inteiro: é
   * ele que sabe em que buraco ela cabe e o que saiu do lugar por causa dela.
   */
  const marcarNoDia = useCallback(
    async (novo: CompromissoNovo) => {
      await api.marcarCompromisso(novo)
      setAcrescentando(false)
      aoMudarACasa()
    },
    [aoMudarACasa],
  )

  const abrir = useCallback((chave: string) => {
    setAberto(chave)
    setErro(null)
  }, [])

  /**
   * Que dia ela está vendo, em relação ao de hoje.
   *
   * Comparação de texto ISO, que é ordenável e não passa por `Date`: quem diz
   * qual é o dia de hoje é o servidor, e jogar essas duas datas no relógio do
   * aparelho é o que faria uma mãe em viagem ver a segunda como se fosse
   * domingo. Sem `hojeNaCasa` o dia é hoje, que é como as telas antigas —
   * e os testes — montam esta tela.
   */
  const passou = Boolean(hojeNaCasa && dia.hoje < hojeNaCasa)
  const futuro = Boolean(hojeNaCasa && dia.hoje > hojeNaCasa)
  const ehHoje = !passou && !futuro

  const dose = proximaDose(dia.doses)
  const linhas = montarODia(dia)

  return (
    <>
      {/* O cartão é da próxima dose, e "a próxima" é coisa do agora: num dia
          que acabou ela não existe, e num dia que ainda vem o "Dei" registraria
          um remédio que a criança não tomou — o pior botão do produto para se
          errar (regra 1). Quantas doses já foram e quantas faltam continua na
          tela Remédios, que é onde o tratamento mora. */}
      {/* `key` pela dose, e não pela posição.

          Sem ela, anotar a dose das 15h deixava o botão preso em "Anotando…"
          para sempre: o cartão avançava para a das 23h — a mãe via a dose
          certa — mas o React reaproveitava o mesmo componente, e com ele o
          estado `dando` que nunca voltou a ser falso. À noite ela tocava e não
          acontecia nada. O único jeito de destravar era sair da tela e voltar,
          e ninguém adivinha isso.

          É a mesma lição das linhas do dia: identidade, nunca posição. */}
      {ehHoje && dose && <CartaoDeDose key={dose.id ?? 'dose'} dose={dose} aoDar={darADose} />}

      <span className="eyebrow">Seu dia</span>

      {linhas.length === 0 ? (
        // Num dia que não é hoje, "Aproveita" não cabe: no que passou o convite
        // chega tarde, e no que ainda vem ele é palpite sobre um dia que
        // ninguém viveu. A frase constata, e não diz que faltou alguma coisa
        // ali — um dia vazio que passou é um dia vazio, não um dia perdido.
        <p className="vazio">
          {ehHoje ? 'Hoje não tem nada marcado. Aproveita.' : 'Nada marcado nesse dia.'}
        </p>
      ) : (
        <div className="dia">
          {linhas.map((linha, indice) => {
            if (linha.tipo === 'tarefa') {
              return (
                <Tarefa
                  key={linha.item.id ?? `tarefa-${indice}`}
                  item={linha.item}
                  jaFoi={!futuro}
                  ehHoje={ehHoje}
                  aoMarcarFeito={aoMarcarFeito}
                  aoDesmarcar={aoDesmarcar}
                  aoAdiar={aoAdiar}
                />
              )
            }
            if (linha.tipo === 'bloco') {
              // Pela identidade, nunca pela posição. Com `bloco-${indice}` o
              // React reaproveitava a linha ao apagar uma coisa do dia: a lista
              // encolhia, a posição 3 passava a ser outro bloco, e o estado da
              // linha antiga ficava lá.
              const chave = `bloco-${linha.bloco.id ?? indice}`
              return (
                <BlocoDaRotina
                  key={chave}
                  bloco={linha.bloco}
                  aberto={aberto === chave}
                  erro={erro?.onde === chave ? erro.frase : null}
                  aoAbrir={() => abrir(chave)}
                  aoFechar={() => setAberto(null)}
                  aoMexer={(chamada) => aoMexer(chamada, chave)}
                />
              )
            }
            const chave = `compromisso-${linha.compromisso.id ?? indice}`
            return (
              <CompromissoDoDia
                key={chave}
                compromisso={linha.compromisso}
                data={dia.hoje}
                jaFoi={!futuro}
                aberto={aberto === chave}
                erro={erro?.onde === chave ? erro.frase : null}
                aoAbrir={() => abrir(chave)}
                aoFechar={() => setAberto(null)}
                aoMexer={(chamada) => aoMexer(chamada, chave)}
              />
            )
          })}
        </div>
      )}

      {/* Marcar que aconteceu vale no passado — é para isso que ela volta aqui.
          Convidar a **pôr** uma coisa num dia que já foi, não: é a mesma decisão
          que a Semana tomou depois de oferecer "marcar uma coisa" numa segunda
          de agosto. Quem quiser registrar uma coisa que passou tem a tela
          Compromissos, com o campo de data, e tem a conversa. */}
      {!passou &&
        (acrescentando ? (
          <Acrescentar
            dia={dia}
            ehHoje={ehHoje}
            aoMarcar={marcarNoDia}
            aoFechar={() => setAcrescentando(false)}
          />
        ) : (
          <>
            <button type="button" className="convite" onClick={() => setAcrescentando(true)}>
              <Icone nome="somar" tamanho={20} />
              {ehHoje ? 'Marcar uma coisa hoje' : 'Marcar uma coisa nesse dia'}
            </button>
            {/* Falar continua sendo o caminho mais curto, e por isso a frase vem
                como frase e não como um segundo botão do mesmo tamanho: dois
                convites empilhados são uma decisão a mais antes de fazer a coisa.
                Com o formulário aberto ela some — ela já está no meio de uma
                coisa, e "ou fala" ali vira ruído. */}
            <p className="vazio">Ou fala pra Ruth: pediatra da Ana quinta às 15h30.</p>
          </>
        ))}

      {/* O mercado é o de agora, não o daquele dia: a lista de compras é uma
          só e vive no presente. Debaixo de um sábado de agosto, "faltam 2
          coisas no mercado" seria a tela dizendo que faltava leite naquele
          sábado — e ela sairia de casa por causa de uma frase que não é sobre
          o dia que está lendo. */}
      {ehHoje && dia.compras.length > 0 && (
        <button type="button" className="cartao compras" onClick={aoAbrirCompras}>
          <span className="compras__icone">
            <Icone nome="carrinho" />
          </span>
          <span className="compras__texto">
            {dia.compras.length === 1
              ? 'Falta 1 coisa no mercado'
              : `Faltam ${dia.compras.length} coisas no mercado`}
          </span>
          <span className="compras__seta">
            <Icone nome="seta" tamanho={18} />
          </span>
        </button>
      )}
    </>
  )
}
